import { D } from '../decimal.js';
import { FUNCTIONS, isFunctionName, type CompareOp, type Node } from './ast.js';
import { FormulaError } from './errors.js';

const MAX_LENGTH = 2000;
const MAX_DEPTH = 64;

type Token =
  | { t: 'num'; raw: string; unit?: string; pos: number }
  | { t: 'ident'; name: string; pos: number }
  | { t: 'op'; op: string; pos: number }
  | { t: 'eof'; pos: number };

const OP_ALIASES: Record<string, string> = { '×': '*', '·': '*', '÷': '/', '−': '-', '–': '-' };

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) { i++; continue; }
    // Nombre (point décimal uniquement : la virgule sépare les arguments).
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const m = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(i))!;
      const pos = i;
      i += m[0].length;
      let unit: string | undefined;
      let j = i;
      while (src[j] === ' ') j++;
      if (src[j] === '[') {
        const end = src.indexOf(']', j);
        if (end < 0) throw new FormulaError('SYNTAX', 'Crochet « ] » manquant après l\'unité', j);
        unit = src.slice(j + 1, end).trim();
        if (!unit) throw new FormulaError('SYNTAX', 'Unité vide entre crochets', j);
        i = end + 1;
      }
      tokens.push(unit ? { t: 'num', raw: m[0], unit, pos } : { t: 'num', raw: m[0], pos });
      continue;
    }
    const id = /^[\p{L}_][\p{L}\p{N}_.]*/u.exec(src.slice(i));
    if (id) {
      if (id[0].endsWith('.')) throw new FormulaError('SYNTAX', `Identifiant invalide « ${id[0]} »`, i);
      tokens.push({ t: 'ident', name: id[0], pos: i });
      i += id[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['<=', '>=', '==', '!='].includes(two)) { tokens.push({ t: 'op', op: two, pos: i }); i += 2; continue; }
    const op = OP_ALIASES[c] ?? c;
    if ('+-*/^(),<>'.includes(op)) { tokens.push({ t: 'op', op, pos: i }); i++; continue; }
    throw new FormulaError('SYNTAX', `Caractère inattendu « ${c} »`, i);
  }
  tokens.push({ t: 'eof', pos: src.length });
  return tokens;
}

/**
 * Analyse une formule ExpertDevis BTP en arbre syntaxique.
 *
 * Grammaire : comparaison < (+ −) < (× ÷) < moins unaire < puissance (associative à droite).
 * Littéraux avec unité : `0.20[m]`, `350[kg/m3]`. Fonctions : liste fermée (voir FUNCTIONS).
 */
export function parseFormula(source: string): Node {
  if (source.length > MAX_LENGTH) throw new FormulaError('TOO_COMPLEX', `Formule trop longue (> ${MAX_LENGTH} caractères)`);
  if (!source.trim()) throw new FormulaError('SYNTAX', 'Formule vide', 0);
  const tokens = tokenize(source);
  let p = 0;
  let depth = 0;

  const peek = () => tokens[p]!;
  const next = () => tokens[p++]!;
  const isOp = (op: string) => { const t = peek(); return t.t === 'op' && t.op === op; };
  const expect = (op: string) => {
    const t = next();
    if (t.t !== 'op' || t.op !== op) throw new FormulaError('SYNTAX', `« ${op} » attendu`, t.pos);
  };
  const enter = () => { if (++depth > MAX_DEPTH) throw new FormulaError('TOO_COMPLEX', 'Formule trop imbriquée'); };

  function comparison(): Node {
    const left = additive();
    const t = peek();
    if (t.t === 'op' && ['<', '<=', '>', '>=', '==', '!='].includes(t.op)) {
      next();
      const right = additive();
      return { kind: 'compare', op: t.op as CompareOp, left, right, pos: t.pos };
    }
    return left;
  }

  function additive(): Node {
    let left = multiplicative();
    for (;;) {
      const t = peek();
      if (t.t === 'op' && (t.op === '+' || t.op === '-')) {
        next();
        left = { kind: 'binary', op: t.op, left, right: multiplicative(), pos: t.pos };
      } else return left;
    }
  }

  function multiplicative(): Node {
    let left = unary();
    for (;;) {
      const t = peek();
      if (t.t === 'op' && (t.op === '*' || t.op === '/')) {
        next();
        left = { kind: 'binary', op: t.op, left, right: unary(), pos: t.pos };
      } else return left;
    }
  }

  function unary(): Node {
    const t = peek();
    if (t.t === 'op' && t.op === '-') { next(); enter(); const arg = unary(); depth--; return { kind: 'unary', op: '-', arg, pos: t.pos }; }
    if (t.t === 'op' && t.op === '+') { next(); return unary(); }
    return power();
  }

  function power(): Node {
    const base = primary();
    const t = peek();
    if (t.t === 'op' && t.op === '^') {
      next();
      enter();
      const exponent = unary();
      depth--;
      return { kind: 'binary', op: '^', left: base, right: exponent, pos: t.pos };
    }
    return base;
  }

  function primary(): Node {
    const t = next();
    if (t.t === 'num') {
      const node: Node = { kind: 'num', value: D(t.raw), raw: t.raw, pos: t.pos };
      if (t.unit) node.unit = t.unit;
      return node;
    }
    if (t.t === 'ident') {
      if (isOp('(')) {
        next();
        if (!isFunctionName(t.name)) throw new FormulaError('UNKNOWN_FUNCTION', `Fonction inconnue « ${t.name} »`, t.pos);
        enter();
        const args: Node[] = [];
        if (!isOp(')')) {
          do args.push(comparison()); while (isOp(',') && next());
        }
        expect(')');
        depth--;
        const spec = FUNCTIONS[t.name];
        if (args.length < spec.minArgs || args.length > spec.maxArgs) {
          throw new FormulaError('ARITY', `Nombre d'arguments incorrect pour ${t.name}()`, t.pos);
        }
        return { kind: 'call', name: t.name, args, pos: t.pos };
      }
      if (t.name === 'pi') return { kind: 'call', name: 'pi', args: [], pos: t.pos };
      return { kind: 'ident', name: t.name, pos: t.pos };
    }
    if (t.t === 'op' && t.op === '(') {
      enter();
      const inner = comparison();
      expect(')');
      depth--;
      return inner;
    }
    throw new FormulaError('SYNTAX', t.t === 'eof' ? 'Fin de formule inattendue' : 'Expression attendue', t.pos);
  }

  const ast = comparison();
  const rest = peek();
  if (rest.t !== 'eof') throw new FormulaError('SYNTAX', 'Élément inattendu après la fin de l\'expression', rest.pos);
  return ast;
}
